import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Globe } from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#0F172A] text-white py-4 sm:py-6 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-heading font-semibold">Our Orbis</h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800 gap-2 text-sm sm:text-base"
            data-testid="back-to-dashboard-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Dashboard</span>
            <span className="sm:hidden">Back</span>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="py-8 sm:py-16 px-4 sm:px-8">
        <article className="max-w-[720px] mx-auto">
          <h1 className="text-3xl sm:text-4xl font-heading font-semibold text-[#0F172A] mb-8 sm:mb-12 text-center">
            About
          </h1>

          <div className="space-y-6 text-slate-700 leading-relaxed text-base sm:text-[17px]">
            <p>
              Our Orbis is a personal relationship management tool built for professionals who understand that their network is one of their most valuable assets.
            </p>

            <p>
              In a world of fleeting digital interactions, Our Orbis gives you a dedicated, private space to do something increasingly rare — remember people properly. It is designed to help you log the details that matter: how you met someone, what you discussed, who connected you, what they care about, and what you want to do next.
            </p>

            <p>
              Our Orbis is organised around four core modules:
            </p>

            <p>
              <span className="font-semibold text-[#0F172A]">Contacts</span> is your primary relationship database. Every person you meet can have a rich, detailed profile — covering their background, your meeting notes, your common network, their personal interests, family, and more. As your relationship with someone evolves, you can log every subsequent interaction, either as a quick summary or a full set of detailed notes. Your contacts are always listed alphabetically and fully searchable across every meaningful field.
            </p>

            <p>
              <span className="font-semibold text-[#D4AF37]">Future Network</span> is your personal pipeline of people you have not yet met but intend to. Log who they are, where they are based, and your angle for connecting — then keep it separate and private until the time is right.
            </p>

            <p>
              <span className="font-semibold text-[#D4AF37]">Catch-Up</span> is your reminder list of people you want to reconnect with. Track whether you have reached out, when, and how — so no relationship gets left behind by accident.
            </p>

            <p>
              <span className="font-semibold text-[#0F172A]">Search</span> runs across all the details that matter — names, employers, cities, tags, shared contacts, locations, and more — so the right person is always one search away.
            </p>

            <p>
              Our Orbis was conceived and created by <span className="font-semibold text-[#0F172A]">Tanuj Khosla</span> as a tool built around the way professionals actually build and maintain relationships — thoughtfully, deliberately, and over time.
            </p>

            <p className="italic text-[#0F172A] text-center pt-8 text-lg font-heading">
              Our Orbis. Your world of connections.
            </p>
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-200">
        <div className="max-w-[720px] mx-auto px-4 sm:px-8 text-center text-slate-400 text-sm">
          © {new Date().getFullYear()} Our Orbis
        </div>
      </footer>
    </div>
  );
};

export default About;
